import { ImageStrip } from "@prototypes/image-strip/ImageStrip";
import type {
    Booking,
    BookingInMessage,
    BookingToConfirmInMemberMessage,
} from "../../common/types/Booking";
import { safelyConvertTime } from "../../utils/time.util";
import "./BookingView.css";
import { googleViewInMapUrl } from "../../common/utils/google/GooglePlaceService";
import { viewAttachment } from "../../utils/view-message-attachment.util";
import MessageAttachmentPreview from "../Chat/MessageAttachmentPreview";
import Button from "../Common/Buttons";
import { Flex } from "../Common/Flex";
import Icon, { PictogramIcon } from "../Common/Icon";
import Spacer from "../Common/Spacer";
import { arrangedBookingImages, toBookingStripImages } from "./bookingImageStrip";

type Props = {
    booking: Booking | BookingInMessage | BookingToConfirmInMemberMessage | undefined;
    orderNumber?: number;
    onViewBookingClick?: () => void;
};

const BookingView = function BookingView(props: Props) {
    const { booking, orderNumber, onViewBookingClick } = props;
    const prefix = orderNumber ? `Option ${orderNumber}: ` : "";
    const title = booking?.title ? `${prefix}${booking.title}` : "";
    if (!booking) return <div />;

    const stripImages = toBookingStripImages(arrangedBookingImages(booking));

    return (
        <div className="booking-view">
            <h1>{title}</h1>
            <div className="booking-view__content">
                <div className="booking-view__left">
                    <ImageStrip images={stripImages} />
                    <Spacer />
                    <h2>{booking.headline}</h2>
                    <Spacer />
                    <div className="booking-view__detail-section">
                        {booking.description}
                    </div>
                    {booking.benefits && (
                        <>
                            <h3>Albertine benefits</h3>
                            <div className="booking-view__detail-section">
                                {booking.benefits}
                            </div>
                        </>
                    )}
                    {booking.bookingPolicy && (
                        <>
                            <h3>Booking policy</h3>
                            <div className="booking-view__detail-section">
                                {booking.bookingPolicy}
                            </div>
                        </>
                    )}
                    <h3>Contact</h3>
                    <Flex.Column gap="medium" className="booking-view__detail-section">
                        {booking.website && (
                            <Button.Ghost
                                onClick={() =>
                                    window.open(booking.website ?? "", "_blank")
                                }
                            >
                                <Icon.Link.Medium />
                                {booking.website}
                            </Button.Ghost>
                        )}
                        {booking.phoneNumber && (
                            <Button.Ghost
                                onClick={() =>
                                    window.open(`tel:${booking.phoneNumber}`)
                                }
                            >
                                <Icon.Call.Medium />
                                {booking.phoneNumber}
                            </Button.Ghost>
                        )}
                        {booking.address && (
                            <Button.Ghost
                                onClick={() =>
                                    window.open(
                                        googleViewInMapUrl(booking as Booking),
                                        "_blank",
                                    )
                                }
                            >
                                <Icon.LocationOn.Medium />
                                {booking.address}
                            </Button.Ghost>
                        )}
                    </Flex.Column>
                    {booking.notesForMember && booking.notesForMemberSenderName && (
                        <>
                            <h3>
                                {`Personal note from ${booking.notesForMemberSenderName}`}
                            </h3>
                            <div className="booking-view__detail-section">
                                {booking.notesForMember}
                            </div>
                        </>
                    )}
                    {booking.attachments && booking.attachments.length > 0 && (
                        <>
                            <h3>Attachments</h3>
                            <div className="booking-view__detail-section">
                                <Flex.Column gap="medium">
                                    {booking.attachments?.map((attachment) => (
                                        <MessageAttachmentPreview
                                            key={attachment.id}
                                            title={attachment.filename}
                                            subtitle={attachment.mimeType}
                                            image={
                                                attachment.mimeType?.startsWith(
                                                    "image/",
                                                )
                                                    ? attachment.url
                                                    : undefined
                                            }
                                            pictogram={
                                                attachment.mimeType ===
                                                "application/pdf" ? (
                                                    <PictogramIcon.Description.Medium.S90 />
                                                ) : undefined
                                            }
                                            onClick={() => {
                                                void viewAttachment(attachment);
                                            }}
                                        />
                                    ))}
                                </Flex.Column>
                            </div>
                        </>
                    )}
                </div>

                <div className="booking-view__right">
                    <div>
                        <h3>Booking details</h3>
                        <div className="booking-view__detail-section">
                            {booking.startTime && (
                                <div>
                                    <div className="booking-view__detail-value">
                                        <div>
                                            Start time{" "}
                                            <span className="booking-view__detail-secondary">
                                                {booking.startTimezone}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="booking-view__detail-value">
                                        <div>
                                            {safelyConvertTime(
                                                booking.startTime,
                                                booking.startTimezone,
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {booking.endTime && (
                                <div className="booking-view__detail-section">
                                    <div className="booking-view__detail-value">
                                        <div>
                                            End time{" "}
                                            <span className="booking-view__detail-secondary">
                                                {booking.endTimezone}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="booking-view__detail-value">
                                        <div>
                                            {safelyConvertTime(
                                                booking.endTime,
                                                booking.endTimezone,
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {booking.offeringCategory === "accommodation" ? (
                                <div>
                                    Duration:{" "}
                                    {`${booking.numberOfNights} night${
                                        booking.numberOfNights !== 1 ? "s" : ""
                                    }`}
                                </div>
                            ) : null}
                            {booking.numberOfGuests && (
                                <div>Guests: {booking.numberOfGuests}</div>
                            )}

                            {booking.rooms && <div>Room: {booking.rooms}</div>}
                        </div>

                        {booking.reference && (
                            <div className="booking-view__detail-section">
                                <div>Reference</div>
                                <div>{booking.reference}</div>
                            </div>
                        )}

                        {booking.price && (
                            <div className="booking-view__detail-section">
                                <div>Price</div>
                                <div>{`${booking.price.amount} ${booking.price.currency}`}</div>
                            </div>
                        )}

                        {onViewBookingClick ? (
                            <div className="booking-view__banner">
                                {onViewBookingClick && (
                                    <Button.N10
                                        onClick={onViewBookingClick}
                                        label="Edit booking details"
                                    />
                                )}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BookingView;
